// Vendor Portal — compliance document uploads.
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, FileText, ShieldCheck, ExternalLink } from "lucide-react";
import vendorApi from "@/lib/vendorApi";
import { useVendor } from "@/pages/vendor/VendorLayout";
import { MULTI_KINDS, SINGLE_KINDS, MAX_UPLOAD_KB, ACCEPT_MIME } from "@/pages/vendor/vendorConfig";

function UploadTile({ kindKey, title, hint, disabled, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_UPLOAD_KB * 1024) {
      toast.error(`File must be ${MAX_UPLOAD_KB} KB (5 MB) or smaller`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kindKey);
      fd.append("file", f, f.name);
      const { data } = await vendorApi.post("/vendor/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      onUploaded?.(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full cursor-pointer transition-colors ${disabled ? "bg-[#E9E2D2] text-[#8a8478] cursor-not-allowed" : "bg-[#0D5C63] hover:bg-[#083E43] text-white"}`} title={hint} data-testid={`vendor-upload-${kindKey}`}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
      {busy ? "Uploading…" : "Upload File"}
      <input type="file" accept={ACCEPT_MIME} hidden disabled={disabled || busy} onChange={onPick} data-testid={`vendor-upload-input-${kindKey}`} />
    </label>
  );
}

function AttachmentRow({ item, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-[#E9E2D2] rounded-lg px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <FileText size={14} className="text-[#0D5C63] shrink-0" />
        <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-[#1A1A1A] hover:text-[#0D5C63] truncate underline-offset-2 hover:underline" title={item.filename || item.url}>
          {item.filename || "Attachment"}
        </a>
        <a href={item.url} target="_blank" rel="noreferrer" aria-label="Open"><ExternalLink size={12} className="text-[#5B5B5B]" /></a>
        <span className="text-[10px] text-[#8a8478] shrink-0 ml-1">
          {item.uploaded_at ? new Date(item.uploaded_at).toLocaleDateString() : ""}
        </span>
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="text-red-700 hover:bg-red-50 rounded p-1"
          aria-label="Remove"
          data-testid={`vendor-attachment-remove-${item.id || item.public_id || "single"}`}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export default function VendorDocuments() {
  const { reg, mustChange, reload } = useVendor();

  const deleteMulti = async (field, item_id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await vendorApi.delete(`/vendor/attachments/${field}/${item_id}`);
      toast.success("Attachment removed");
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not delete");
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="vendor-documents-card">
      {/* Registration-submitted docs (read-only) */}
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-7" data-testid="vendor-reg-docs-card">
        <div className="flex items-center gap-2 text-[#0D5C63]">
          <FileText size={18} />
          <h2 className="trust-serif text-xl">Submitted at Registration</h2>
        </div>
        <p className="text-xs text-[#8a8478] mt-1">These were uploaded during your vendor registration and are on file with GS Mandal.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="border border-[#E9E2D2] rounded-lg p-4 bg-[#FAFAF7]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">PAN Card Copy</div>
            {reg.pan_card_url ? (
              <a href={reg.pan_card_url} target="_blank" rel="noreferrer" data-testid="vendor-reg-pan-link" className="mt-2 inline-flex items-center gap-2 bg-white border border-[#E9E2D2] hover:border-[#0D5C63] px-3 py-2 rounded text-xs text-[#1A1A1A]">
                <FileText size={14} className="text-[#0D5C63]" />
                <span>View PAN Card</span>
                <ExternalLink size={12} className="text-[#5B5B5B]" />
              </a>
            ) : <div className="text-[11px] text-[#8a8478] mt-2 italic">Not on file.</div>}
          </div>
          <div className="border border-[#E9E2D2] rounded-lg p-4 bg-[#FAFAF7]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">{reg.gst_is_declaration ? "GST — Not Applicable Declaration" : "GST Registration Certificate"}</div>
            {reg.gst_certificate_url ? (
              <a href={reg.gst_certificate_url} target="_blank" rel="noreferrer" data-testid="vendor-reg-gst-link" className="mt-2 inline-flex items-center gap-2 bg-white border border-[#E9E2D2] hover:border-[#0D5C63] px-3 py-2 rounded text-xs text-[#1A1A1A]">
                <FileText size={14} className="text-[#0D5C63]" />
                <span>{reg.gst_is_declaration ? "View Declaration" : "View GST Certificate"}</span>
                <ExternalLink size={12} className="text-[#5B5B5B]" />
              </a>
            ) : <div className="text-[11px] text-[#8a8478] mt-2 italic">Not on file.</div>}
          </div>
        </div>
      </section>

      {/* Compliance uploads (portal) */}
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-7">
        <div className="flex items-center gap-2 text-[#0D5C63]">
          <ShieldCheck size={18} />
          <h2 className="trust-serif text-xl">Compliance Documents</h2>
        </div>
        <p className="text-xs text-[#8a8478] mt-1">Accepted formats: PDF, JPG, PNG · Max size 5 MB per file.</p>

        {mustChange && (
          <div className="mt-4 bg-[#FAFAF7] border border-dashed border-[#E9E2D2] rounded-lg p-4 text-xs text-[#8a8478]">
            Please change your temporary password to enable uploads.
          </div>
        )}

        <div className="mt-5 space-y-6">
          {MULTI_KINDS.map((k) => {
            const items = reg[k.field] || [];
            return (
              <div key={k.key} className="border-b border-[#E9E2D2] pb-6 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold uppercase tracking-widest">{k.title}</h3>
                    <p className="text-[11px] text-[#8a8478] mt-0.5">{k.hint}</p>
                  </div>
                  <UploadTile kindKey={k.key} title={k.title} hint={k.hint} disabled={mustChange} onUploaded={reload} />
                </div>
                <div className="mt-3 space-y-2">
                  {items.length === 0 ? (
                    <div className="text-[11px] text-[#8a8478]">No uploads yet.</div>
                  ) : items.map((it) => (
                    <AttachmentRow key={it.id || it.public_id} item={it} onDelete={() => deleteMulti(k.field, it.id)} />
                  ))}
                </div>
              </div>
            );
          })}

          <div className="grid md:grid-cols-2 gap-6">
            {SINGLE_KINDS.map((k) => {
              const it = reg[k.field] || {};
              return (
                <div key={k.key} className="border border-[#E9E2D2] rounded-lg p-4 bg-[#FAFAF7]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold uppercase tracking-widest">{k.title}</h3>
                      <p className="text-[11px] text-[#8a8478] mt-0.5">{k.hint}</p>
                    </div>
                    <UploadTile kindKey={k.key} title={k.title} hint={k.hint} disabled={mustChange} onUploaded={reload} />
                  </div>
                  <div className="mt-3">
                    {it.url ? (
                      <AttachmentRow item={{ ...it, id: k.key }} />
                    ) : (
                      <div className="text-[11px] text-[#8a8478]">Not uploaded yet.</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
